import { genericTasksService } from './generic-tasks.service';
import { getTaskTemplatesForEventType, TaskTemplate } from '../../constants/event-task-templates';
import { 
  LegalTaskTemplate, 
  getLegalTasksForCountryAndEvent 
} from '../../constants/legal-task-templates';

export class EventTaskGeneratorService {
  // Cache pour éviter les vérifications répétées
  private checkedEvents = new Set<string>();
  
  /**
   * Génère automatiquement les tâches pour un nouvel événement
   * @param eventId ID de l'événement
   * @param eventType Type d'événement
   * @param eventDate Date de l'événement
   * @param country Code pays pour les tâches légales (optionnel)
   */
  async generateTasksForEvent(
    eventId: string, 
    eventType: string, 
    eventDate: Date, 
    country?: string,
    userLanguage: string = 'fr'
  ): Promise<void> {
    try {
      // Vérifier les paramètres d'entrée
      if (!eventId) {
        throw new Error('EventId is required');
      }
      if (!eventType) {
        throw new Error('EventType is required');
      }
      if (!eventDate || !(eventDate instanceof Date)) {
        throw new Error('EventDate must be a valid Date object');
      }

      const templates = getTaskTemplatesForEventType(eventType);

      if (templates.length === 0) {
        console.warn(`⚠️ [TaskGenerator] No task templates found for event type: ${eventType}`);
      } else {}

      // Obtenir les tâches légales si un pays est spécifié
      let legalTasks: LegalTaskTemplate[] = [];
      if (country && country !== 'OTHER') {
        legalTasks = getLegalTasksForCountryAndEvent(country, eventType);

        if (legalTasks.length > 0) {}
      } else {}

      const standardTasks = this.generateTasksFromTemplates(templates, eventDate, userLanguage);
      const legalTasksData = this.generateLegalTasksFromTemplates(legalTasks, eventDate, userLanguage);

      // Combiner toutes les tâches
      const allTasksToCreate = [...standardTasks, ...legalTasksData];

      if (allTasksToCreate.length === 0) {
        console.warn(`⚠️ [TaskGenerator] No tasks to create - this might be an issue`);
        return;
      }

      const createdTasks = [];

      const existingTasks = await genericTasksService.getEventTasks(eventId, eventType);

      // Créer un Set des tâches existantes pour une recherche rapide
      const existingTaskKeys = new Set(
        existingTasks.map(task => `${task.title}|${task.category}`)
      );

      for (let i = 0; i < allTasksToCreate.length; i++) {
        const taskData = allTasksToCreate[i];
        const taskKey = `${taskData.title}|${taskData.category}`;
        
        // Vérifier si cette tâche existe déjà
        if (existingTaskKeys.has(taskKey)) {
          continue;
        }
        
        try {
          const taskId = await genericTasksService.createTask(eventId, eventType, taskData);
          createdTasks.push(taskId);

          // Ajouter la tâche créée au Set pour éviter les doublons dans la même session
          existingTaskKeys.add(taskKey);
        } catch (taskError) {
          console.error(`❌ [TaskGenerator] Failed to create task: ${taskData.title}`, taskError);
          console.error(`❌ [TaskGenerator] Task data:`, taskData);
        }
      }

      if (createdTasks.length === 0) {
        console.error(`❌ [TaskGenerator] No tasks were successfully created - this indicates a database issue`);
      }
    } catch (error) {
      console.error(`❌ [TaskGenerator] Error generating tasks for event ${eventId}:`, error);
      console.error(`❌ [TaskGenerator] Error stack:`, error instanceof Error ? error.stack : 'No stack trace');
      throw error;
    }
  }
  
  /**
   * Génère les données de tâches à partir des templates
   * @param templates Templates de tâches
   * @param eventDate Date de l'événement
   * @returns Array des données de tâches à créer
   */
  private generateTasksFromTemplates(templates: TaskTemplate[], eventDate: Date, userLanguage: string = 'fr'): any[] {
    const now = new Date();
    
    return templates.map(template => {
      // Calculer la date d'échéance
      const dueDate = new Date(eventDate);
      dueDate.setDate(dueDate.getDate() - template.daysBeforeEvent);
      
      // Si la date d'échéance est dans le passé, l'ajuster à aujourd'hui + quelques jours
      const adjustedDueDate = dueDate < now 
        ? new Date(now.getTime() + (3 * 24 * 60 * 60 * 1000)) // 3 jours à partir d'aujourd'hui
        : dueDate;
      
      // Utiliser les traductions hardcodées directement
      const getTranslation = (key: string, type: 'title' | 'description' = 'title'): string => {
        const translations: Record<string, Record<string, Record<string, string>>> = {
          fr: {
            'Définir le budget global': {
              title: 'Définir le budget global',
              description: 'Établir un budget détaillé pour chaque poste de dépense'
            },
            'Choisir la date du mariage': {
              title: 'Choisir la date du mariage',
              description: 'Fixer la date du mariage en fonction des disponibilités du lieu et des prestataires principaux'
            },
            'Rechercher le lieu de réception': {
              title: 'Rechercher le lieu de réception',
              description: 'Rechercher et visiter différents lieux pour la cérémonie et la réception'
            },
            'Réserver les prestataires principaux': {
              title: 'Réserver les prestataires principaux',
              description: 'Réserver les prestataires essentiels : photographe, traiteur, fleuriste, musique'
            },
            'Commander les tenues': {
              title: 'Commander les tenues',
              description: 'Acheter la robe de mariée et le costume du marié'
            },
            'Choisir les témoins': {
              title: 'Choisir les témoins',
              description: 'Sélectionner les témoins pour la cérémonie civile et religieuse'
            },
            'Commander les faire-part': {
              title: 'Commander les faire-part',
              description: 'Concevoir et commander les invitations de mariage'
            },
            'Planifier le menu': {
              title: 'Planifier le menu',
              description: 'Définir le menu de la réception avec le traiteur'
            },
            'Réserver les hébergements': {
              title: 'Réserver les hébergements',
              description: 'Réserver les chambres pour les invités venant de loin'
            },
            'Envoyer les faire-part': {
              title: 'Envoyer les faire-part',
              description: 'Concevoir et envoyer les invitations de mariage aux invités'
            },
            'Finaliser la décoration': {
              title: 'Finaliser la décoration',
              description: 'Finaliser tous les détails de décoration du lieu'
            },
            'Essayage final des tenues': {
              title: 'Essayage final des tenues',
              description: 'Dernier essayage de la robe et du costume avant le mariage'
            },
            'Confirmer les détails avec les prestataires': {
              title: 'Confirmer les détails avec les prestataires',
              description: 'Confirmer tous les détails avec chaque prestataire'
            },
            // Birthday
            'Choisir le thème de la fête': { title: 'Choisir le thème de la fête', description: 'Définir le thème et l\'ambiance de l\'anniversaire' },
            'Réserver le lieu': { title: 'Réserver le lieu', description: 'Trouver et réserver l\'endroit pour célébrer' },
            'Établir la liste des invités': { title: 'Établir la liste des invités', description: 'Dresser la liste des personnes à inviter' },
            'Envoyer les invitations': { title: 'Envoyer les invitations', description: 'Envoyer les invitations par email, SMS ou courrier' },
            'Commander le gâteau': { title: 'Commander le gâteau', description: 'Choisir et commander le gâteau d\'anniversaire' },
            'Planifier les activités': { title: 'Planifier les activités', description: 'Organiser les jeux, animations et divertissements' },
            'Acheter les décorations': { title: 'Acheter les décorations', description: 'Ballons, guirlandes, et autres éléments décoratifs' },
            'Prévoir la nourriture et boissons': { title: 'Prévoir la nourriture et boissons', description: 'Planifier le menu et faire les courses' },
            'Confirmer les présences': { title: 'Confirmer les présences', description: 'Relancer les invités qui n\'ont pas encore répondu' },
            'Préparer la playlist': { title: 'Préparer la playlist', description: 'Sélectionner la musique pour l\'ambiance' },
            // Engagement
            'Choisir la date et l\'heure': { title: 'Choisir la date et l\'heure', description: 'Fixer le moment pour annoncer les fiançailles' },
            'Organiser la décoration': { title: 'Organiser la décoration', description: 'Prévoir une décoration romantique et élégante' },
            'Prévoir le photographe': { title: 'Prévoir le photographe', description: 'Organiser la prise de photos pour immortaliser le moment' },
            'Préparer le discours': { title: 'Préparer le discours', description: 'Réfléchir aux mots pour annoncer les fiançailles' },
            // Baby Shower
            'Choisir le thème': { title: 'Choisir le thème', description: 'Sélectionner un thème mignon pour le baby shower' },
            'Fixer la date': { title: 'Fixer la date', description: 'Choisir une date qui convient à la future maman' },
            'Créer la liste de naissance': { title: 'Créer la liste de naissance', description: 'Aider à établir une liste de cadeaux utiles' },
            'Organiser les jeux': { title: 'Organiser les jeux', description: 'Préparer des activités amusantes sur le thème bébé' },
            // Baptism
            'Contacter l\'église/lieu de cérémonie': { title: 'Contacter l\'église/lieu de cérémonie', description: 'Réserver la date et organiser la cérémonie religieuse' },
            'Choisir les parrains/marraines': { title: 'Choisir les parrains/marraines', description: 'Sélectionner et demander aux futurs parrains et marraines' },
            'Organiser la réception': { title: 'Organiser la réception', description: 'Réserver le lieu pour la fête après la cérémonie' },
            'Choisir la tenue de baptême': { title: 'Choisir la tenue de baptême', description: 'Sélectionner la robe ou tenue traditionnelle' },
            // Other
            'Définir l\'objectif de l\'événement': { title: 'Définir l\'objectif de l\'événement', description: 'Clarifier le but et le type de célébration' },
            'Fixer la date et l\'heure': { title: 'Fixer la date et l\'heure', description: 'Choisir le moment optimal pour l\'événement' },
            'Planifier le programme': { title: 'Planifier le programme', description: 'Organiser le déroulement de l\'événement' },
            'Organiser la restauration': { title: 'Organiser la restauration', description: 'Prévoir nourriture et boissons adaptées' },
            'Préparer la décoration': { title: 'Préparer la décoration', description: 'Décorer selon le thème de l\'événement' }
          },
          en: {
            'Définir le budget global': {
              title: 'Define overall budget',
              description: 'Establish a detailed budget for each expense item'
            },
            'Choisir la date du mariage': {
              title: 'Choose wedding date',
              description: 'Set the wedding date based on venue and main vendors availability'
            },
            'Rechercher le lieu de réception': {
              title: 'Find reception venue',
              description: 'Research and visit different venues for the ceremony and reception'
            },
            'Réserver les prestataires principaux': {
              title: 'Book main vendors',
              description: 'Book essential vendors: photographer, caterer, florist, music'
            },
            'Commander les tenues': {
              title: 'Order attire',
              description: 'Shop for wedding dress and groom\'s suit'
            },
            'Choisir les témoins': {
              title: 'Choose witnesses',
              description: 'Select witnesses for civil and religious ceremony'
            },
            'Commander les faire-part': {
              title: 'Order invitations',
              description: 'Design and order wedding invitations'
            },
            'Planifier le menu': {
              title: 'Plan menu',
              description: 'Define the reception menu with the caterer'
            },
            'Réserver les hébergements': {
              title: 'Book accommodations',
              description: 'Reserve rooms for guests coming from far away'
            },
            'Envoyer les faire-part': {
              title: 'Send invitations',
              description: 'Design and send wedding invitations to guests'
            },
            'Finaliser la décoration': {
              title: 'Finalize decoration',
              description: 'Finalize all venue decoration details'
            },
            'Essayage final des tenues': {
              title: 'Final attire fitting',
              description: 'Last fitting for dress and suit before the wedding'
            },
            'Confirmer les détails avec les prestataires': {
              title: 'Confirm details with vendors',
              description: 'Confirm all details with each vendor'
            },
            // Birthday
            'Choisir le thème de la fête': { title: 'Choose party theme', description: 'Define the theme and atmosphere of the birthday' },
            'Réserver le lieu': { title: 'Book the venue', description: 'Find and book the place to celebrate' },
            'Établir la liste des invités': { title: 'Create guest list', description: 'Make a list of people to invite' },
            'Envoyer les invitations': { title: 'Send invitations', description: 'Send invitations by email, SMS or mail' },
            'Commander le gâteau': { title: 'Order the cake', description: 'Choose and order the birthday cake' },
            'Planifier les activités': { title: 'Plan activities', description: 'Organize games and entertainment' },
            'Acheter les décorations': { title: 'Buy decorations', description: 'Balloons, garlands, and other decorative elements' },
            'Prévoir la nourriture et boissons': { title: 'Plan food and drinks', description: 'Plan the menu and do the groceries' },
            'Confirmer les présences': { title: 'Confirm attendance', description: 'Follow up with guests who haven\'t replied yet' },
            'Préparer la playlist': { title: 'Prepare playlist', description: 'Select music for the atmosphere' },
            // Engagement
            'Choisir la date et l\'heure': { title: 'Choose date and time', description: 'Set the moment to announce the engagement' },
            'Organiser la décoration': { title: 'Organize decoration', description: 'Plan a romantic and elegant decoration' },
            'Prévoir le photographe': { title: 'Book photographer', description: 'Organize photoshoot to immortalize the moment' },
            'Préparer le discours': { title: 'Prepare speech', description: 'Think about the words to announce the engagement' },
            // Baby Shower
            'Choisir le thème': { title: 'Choose theme', description: 'Select a cute theme for the baby shower' },
            'Fixer la date': { title: 'Set the date', description: 'Choose a date that suits the mom-to-be' },
            'Créer la liste de naissance': { title: 'Create baby registry', description: 'Help establish a list of useful gifts' },
            'Organiser les jeux': { title: 'Organize games', description: 'Prepare fun baby-themed activities' },
            // Baptism
            'Contacter l\'église/lieu de cérémonie': { title: 'Contact church/ceremony venue', description: 'Book the date and organize the religious ceremony' },
            'Choisir les parrains/marraines': { title: 'Choose godparents', description: 'Select and ask the future godparents' },
            'Organiser la réception': { title: 'Organize reception', description: 'Book the venue for the party after the ceremony' },
            'Choisir la tenue de baptême': { title: 'Choose baptism outfit', description: 'Select the traditional dress or outfit' },
            // Other
            'Définir l\'objectif de l\'événement': { title: 'Define event objective', description: 'Clarify the goal and type of celebration' },
            'Fixer la date et l\'heure': { title: 'Set date and time', description: 'Choose the optimal moment for the event' },
            'Planifier le programme': { title: 'Plan the program', description: 'Organize the flow of the event' },
            'Organiser la restauration': { title: 'Organize catering', description: 'Plan appropriate food and drinks' },
            'Préparer la décoration': { title: 'Prepare decoration', description: 'Decorate according to the event theme' }
          },
          es: {
            'Définir le budget global': {
              title: 'Definir presupuesto general',
              description: 'Establecer un presupuesto detallado para cada partida de gastos'
            },
            'Choisir la date du mariage': {
              title: 'Elegir fecha de boda',
              description: 'Fijar la fecha de la boda según la disponibilidad del lugar y los proveedores principales'
            },
            'Rechercher le lieu de réception': {
              title: 'Buscar lugar de recepción',
              description: 'Investigar y visitar diferentes lugares para la ceremonia y la recepción'
            },
            'Réserver les prestataires principaux': {
              title: 'Reservar proveedores principales',
              description: 'Reservar proveedores esenciales: fotógrafo, catering, florista, música'
            },
            'Commander les tenues': {
              title: 'Encargar vestimenta',
              description: 'Comprar el vestido de novia y el traje del novio'
            },
            'Choisir les témoins': {
              title: 'Elegir testigos',
              description: 'Seleccionar testigos para la ceremonia civil y religiosa'
            },
            'Commander les faire-part': {
              title: 'Encargar invitaciones',
              description: 'Diseñar y encargar las invitaciones de boda'
            },
            'Planifier le menu': {
              title: 'Planificar menú',
              description: 'Definir el menú de la recepción con el catering'
            },
            'Réserver les hébergements': {
              title: 'Reservar alojamientos',
              description: 'Reservar habitaciones para invitados que vienen de lejos'
            },
            'Envoyer les faire-part': {
              title: 'Enviar invitaciones',
              description: 'Diseñar y enviar invitaciones de boda a los invitados'
            },
            'Finaliser la décoration': {
              title: 'Finalizar decoración',
              description: 'Finalizar todos los detalles de decoración del lugar'
            },
            'Essayage final des tenues': {
              title: 'Prueba final de vestimenta',
              description: 'Última prueba del vestido y traje antes de la boda'
            },
            'Confirmer les détails avec les prestataires': {
              title: 'Confirmar detalles con proveedores',
              description: 'Confirmar todos los detalles con cada proveedor'
            },
            // Birthday
            'Choisir le thème de la fête': { title: 'Elegir tema de la fiesta', description: 'Definir el tema y la atmósfera del cumpleaños' },
            'Réserver le lieu': { title: 'Reservar el lugar', description: 'Encontrar y reservar el lugar para celebrar' },
            'Établir la liste des invités': { title: 'Crear lista de invitados', description: 'Hacer una lista de personas a invitar' },
            'Envoyer les invitations': { title: 'Enviar invitaciones', description: 'Enviar invitaciones por correo electrónico, SMS o correo postal' },
            'Commander le gâteau': { title: 'Encargar el pastel', description: 'Elegir y encargar el pastel de cumpleaños' },
            'Planifier les activités': { title: 'Planificar actividades', description: 'Organizar juegos y entretenimiento' },
            'Acheter les décorations': { title: 'Comprar decoraciones', description: 'Globos, guirnaldas y otros elementos decorativos' },
            'Prévoir la nourriture et boissons': { title: 'Planificar comida y bebida', description: 'Planificar el menú y hacer las compras' },
            'Confirmer les présences': { title: 'Confirmar asistencia', description: 'Hacer seguimiento a los invitados que aún no han respondido' },
            'Préparer la playlist': { title: 'Preparar lista de reproducción', description: 'Seleccionar música para la atmósfera' },
            // Engagement
            'Choisir la date et l\'heure': { title: 'Elegir fecha y hora', description: 'Fijar el momento para anunciar el compromiso' },
            'Organiser la décoration': { title: 'Organizar decoración', description: 'Planificar una decoración romántica y elegante' },
            'Prévoir le photographe': { title: 'Contratar fotógrafo', description: 'Organizar sesión de fotos para inmortalizar el momento' },
            'Préparer le discours': { title: 'Preparar discurso', description: 'Pensar en las palabras para anunciar el compromiso' },
            // Baby Shower
            'Choisir le thème': { title: 'Elegir tema', description: 'Seleccionar un tema lindo para el baby shower' },
            'Fixer la date': { title: 'Fijar la fecha', description: 'Elegir una fecha que le convenga a la futura mamá' },
            'Créer la liste de naissance': { title: 'Crear lista de regalos', description: 'Ayudar a establecer una lista de regalos útiles' },
            'Organiser les jeux': { title: 'Organizar juegos', description: 'Preparar divertidas actividades temáticas sobre bebés' },
            // Baptism
            'Contacter l\'église/lieu de cérémonie': { title: 'Contactar iglesia/lugar de ceremonia', description: 'Reservar la fecha y organizar la ceremonia religiosa' },
            'Choisir les parrains/marraines': { title: 'Elegir padrinos', description: 'Seleccionar y preguntar a los futuros padrinos' },
            'Organiser la réception': { title: 'Organizar recepción', description: 'Reservar el lugar para la fiesta después de la ceremonia' },
            'Choisir la tenue de baptême': { title: 'Elegir traje de bautizo', description: 'Seleccionar el vestido o traje tradicional' },
            // Other
            'Définir l\'objectif de l\'événement': { title: 'Definir objetivo del evento', description: 'Aclarar el objetivo y el tipo de celebración' },
            'Fixer la date et l\'heure': { title: 'Fijar fecha y hora', description: 'Elegir el momento óptimo para el evento' },
            'Planifier le programme': { title: 'Planificar el programa', description: 'Organizar el desarrollo del evento' },
            'Organiser la restauration': { title: 'Organizar catering', description: 'Planificar comida y bebidas apropiadas' },
            'Préparer la décoration': { title: 'Preparar decoración', description: 'Decorar según el tema del evento' }
          },
          de: {
            'Définir le budget global': {
              title: 'Gesamtbudget definieren',
              description: 'Detailliertes Budget für jeden Ausgabenposten erstellen'
            },
            'Choisir la date du mariage': {
              title: 'Hochzeitsdatum wählen',
              description: 'Hochzeitsdatum basierend auf Verfügbarkeit von Ort und Hauptanbietern festlegen'
            },
            'Rechercher le lieu de réception': {
              title: 'Empfangsort finden',
              description: 'Verschiedene Orte für Zeremonie und Empfang recherchieren und besuchen'
            },
            'Réserver les prestataires principaux': {
              title: 'Hauptanbieter buchen',
              description: 'Wesentliche Anbieter buchen: Fotograf, Catering, Florist, Musik'
            },
            'Commander les tenues': {
              title: 'Kleidung bestellen',
              description: 'Hochzeitskleid und Anzug des Bräutigams einkaufen'
            },
            'Choisir les témoins': {
              title: 'Zeugen wählen',
              description: 'Zeugen für zivile und religiöse Zeremonie auswählen'
            },
            'Commander les faire-part': {
              title: 'Einladungen bestellen',
              description: 'Hochzeitseinladungen entwerfen und bestellen'
            },
            'Planifier le menu': {
              title: 'Menü planen',
              description: 'Empfangsmenü mit dem Catering definieren'
            },
            'Réserver les hébergements': {
              title: 'Unterkünfte buchen',
              description: 'Zimmer für Gäste aus der Ferne reservieren'
            },
            'Envoyer les faire-part': {
              title: 'Einladungen versenden',
              description: 'Hochzeitseinladungen entwerfen und an Gäste versenden'
            },
            'Finaliser la décoration': {
              title: 'Dekoration finalisieren',
              description: 'Alle Dekorationsdetails des Ortes finalisieren'
            },
            'Essayage final des tenues': {
              title: 'Finale Kleidungsanprobe',
              description: 'Letzte Anprobe von Kleid und Anzug vor der Hochzeit'
            },
            'Confirmer les détails avec les prestataires': {
              title: 'Details mit Anbietern bestätigen',
              description: 'Alle Details mit jedem Anbieter bestätigen'
            },
            // Birthday
            'Choisir le thème de la fête': { title: 'Partymotto wählen', description: 'Thema und Atmosphäre des Geburtstags festlegen' },
            'Réserver le lieu': { title: 'Ort buchen', description: 'Ort zum Feiern finden und buchen' },
            'Établir la liste des invités': { title: 'Gästeliste erstellen', description: 'Liste der einzuladenden Personen erstellen' },
            'Envoyer les invitations': { title: 'Einladungen versenden', description: 'Einladungen per E-Mail, SMS oder Post versenden' },
            'Commander le gâteau': { title: 'Kuchen bestellen', description: 'Geburtstagskuchen auswählen und bestellen' },
            'Planifier les activités': { title: 'Aktivitäten planen', description: 'Spiele und Unterhaltung organisieren' },
            'Acheter les décorations': { title: 'Dekoration kaufen', description: 'Luftballons, Girlanden und andere Deko-Elemente' },
            'Prévoir la nourriture et boissons': { title: 'Essen und Trinken planen', description: 'Menü planen und einkaufen' },
            'Confirmer les présences': { title: 'Zusagen bestätigen', description: 'Gäste nachfassen, die noch nicht geantwortet haben' },
            'Préparer la playlist': { title: 'Playlist vorbereiten', description: 'Musik für die Stimmung auswählen' },
            // Engagement
            'Choisir la date et l\'heure': { title: 'Datum und Uhrzeit wählen', description: 'Zeitpunkt für die Bekanntgabe der Verlobung festlegen' },
            'Organiser la décoration': { title: 'Dekoration organisieren', description: 'Eine romantische und elegante Dekoration planen' },
            'Prévoir le photographe': { title: 'Fotograf buchen', description: 'Fotoshooting organisieren, um den Moment festzuhalten' },
            'Préparer le discours': { title: 'Rede vorbereiten', description: 'Über die Worte zur Bekanntgabe der Verlobung nachdenken' },
            // Baby Shower
            'Choisir le thème': { title: 'Thema wählen', description: 'Ein süßes Thema für die Babyparty auswählen' },
            'Fixer la date': { title: 'Datum festlegen', description: 'Ein Datum wählen, das der werdenden Mutter passt' },
            'Créer la liste de naissance': { title: 'Wunschliste erstellen', description: 'Helfen, eine Liste nützlicher Geschenke zusammenzustellen' },
            'Organiser les jeux': { title: 'Spiele organisieren', description: 'Lustige Baby-Spiele vorbereiten' },
            // Baptism
            'Contacter l\'église/lieu de cérémonie': { title: 'Kirche/Zeremonieort kontaktieren', description: 'Datum buchen und die religiöse Zeremonie organisieren' },
            'Choisir les parrains/marraines': { title: 'Paten wählen', description: 'Zukünftige Paten auswählen und fragen' },
            'Organiser la réception': { title: 'Empfang organisieren', description: 'Ort für die Feier nach der Zeremonie buchen' },
            'Choisir la tenue de baptême': { title: 'Taufkleidung wählen', description: 'Traditionelles Kleid oder Outfit auswählen' },
            // Other
            'Définir l\'objectif de l\'événement': { title: 'Veranstaltungsziel definieren', description: 'Ziel und Art der Feier klären' },
            'Fixer la date et l\'heure': { title: 'Datum und Uhrzeit festlegen', description: 'Den optimalen Zeitpunkt für die Veranstaltung wählen' },
            'Planifier le programme': { title: 'Programm planen', description: 'Den Ablauf der Veranstaltung organisieren' },
            'Organiser la restauration': { title: 'Catering organisieren', description: 'Passendes Essen und Getränke planen' },
            'Préparer la décoration': { title: 'Dekoration vorbereiten', description: 'Entsprechend dem Veranstaltungsmotto dekorieren' }
          },
          it: {
            'Définir le budget global': {
              title: 'Definire budget complessivo',
              description: 'Stabilire un budget dettagliato per ogni voce di spesa'
            },
            'Choisir la date du mariage': {
              title: 'Scegliere data matrimonio',
              description: 'Fissare la data del matrimonio in base alla disponibilità del luogo e dei fornitori principali'
            },
            'Rechercher le lieu de réception': {
              title: 'Trovare luogo ricevimento',
              description: 'Ricercare e visitare diversi luoghi per la cerimonia e la reception'
            },
            'Réserver les prestataires principaux': {
              title: 'Prenotare fornitori principali',
              description: 'Prenotare fornitori essenziali: fotografo, catering, fiorista, musica'
            },
            'Commander les tenues': {
              title: 'Ordinare abbigliamento',
              description: 'Acquistare l\'abito da sposa e il completo dello sposo'
            },
            'Choisir les témoins': {
              title: 'Scegliere testimoni',
              description: 'Selezionare testimoni per la cerimonia civile e religiosa'
            },
            'Commander les faire-part': {
              title: 'Ordinare inviti',
              description: 'Progettare e ordinare gli inviti di matrimonio'
            },
            'Planifier le menu': {
              title: 'Pianificare menù',
              description: 'Definire il menù della reception con il catering'
            },
            'Réserver les hébergements': {
              title: 'Prenotare alloggi',
              description: 'Prenotare camere per ospiti che vengono da lontano'
            },
            'Envoyer les faire-part': {
              title: 'Inviare inviti',
              description: 'Progettare e inviare inviti di matrimonio agli ospiti'
            },
            'Finaliser la décoration': {
              title: 'Finalizzare decorazione',
              description: 'Finalizzare tutti i dettagli di decorazione del luogo'
            },
            'Essayage final des tenues': {
              title: 'Prova finale abbigliamento',
              description: 'Ultima prova dell\'abito e completo prima del matrimonio'
            },
            'Confirmer les détails avec les prestataires': {
              title: 'Confermare dettagli con fornitori',
              description: 'Confermare tutti i dettagli con ogni fornitore'
            },
            // Birthday
            'Choisir le thème de la fête': { title: 'Scegliere tema festa', description: 'Definire il tema e l\'atmosfera del compleanno' },
            'Réserver le lieu': { title: 'Prenotare la location', description: 'Trovare e prenotare il luogo per festeggiare' },
            'Établir la liste des invités': { title: 'Creare lista invitati', description: 'Fare una lista delle persone da invitare' },
            'Envoyer les invitations': { title: 'Inviare inviti', description: 'Inviare inviti via email, SMS o posta' },
            'Commander le gâteau': { title: 'Ordinare la torta', description: 'Scegliere e ordinare la torta di compleanno' },
            'Planifier les activités': { title: 'Pianificare attività', description: 'Organizzare giochi e intrattenimento' },
            'Acheter les décorations': { title: 'Comprare decorazioni', description: 'Palloncini, ghirlande e altri elementi decorativi' },
            'Prévoir la nourriture et boissons': { title: 'Pianificare cibo e bevande', description: 'Pianificare il menù e fare la spesa' },
            'Confirmer les présences': { title: 'Confermare presenze', description: 'Ricontattare gli ospiti che non hanno ancora risposto' },
            'Préparer la playlist': { title: 'Preparare playlist', description: 'Selezionare musica per l\'atmosfera' },
            // Engagement
            'Choisir la date et l\'heure': { title: 'Scegliere data e ora', description: 'Fissare il momento per annunciare il fidanzamento' },
            'Organiser la décoration': { title: 'Organizzare decorazione', description: 'Pianificare una decorazione romantica ed elegante' },
            'Prévoir le photographe': { title: 'Prenotare fotografo', description: 'Organizzare servizio fotografico per immortalare il momento' },
            'Préparer le discours': { title: 'Preparare discorso', description: 'Pensare alle parole per annunciare il fidanzamento' },
            // Baby Shower
            'Choisir le thème': { title: 'Scegliere tema', description: 'Selezionare un tema carino per il baby shower' },
            'Fixer la date': { title: 'Fissare la data', description: 'Scegliere una data adatta alla futura mamma' },
            'Créer la liste de naissance': { title: 'Creare lista nascita', description: 'Aiutare a stabilire una lista di regali utili' },
            'Organiser les jeux': { title: 'Organizzare giochi', description: 'Preparare divertenti attività a tema bebè' },
            // Baptism
            'Contacter l\'église/lieu de cérémonie': { title: 'Contattare chiesa/luogo cerimonia', description: 'Prenotare la data e organizzare la cerimonia religiosa' },
            'Choisir les parrains/marraines': { title: 'Scegliere padrini/madrine', description: 'Selezionare e chiedere ai futuri padrini e madrine' },
            'Organiser la réception': { title: 'Organizzare ricevimento', description: 'Prenotare la location per la festa dopo la cerimonia' },
            'Choisir la tenue de baptême': { title: 'Scegliere abito battesimo', description: 'Selezionare il vestito o l\'abito tradizionale' },
            // Other
            'Définir l\'objectif de l\'événement': { title: 'Definire obiettivo evento', description: 'Chiarire lo scopo e il tipo di celebrazione' },
            'Fixer la date et l\'heure': { title: 'Fissare data e ora', description: 'Scegliere il momento ottimale per l\'evento' },
            'Planifier le programme': { title: 'Pianificare il programma', description: 'Organizzare lo svolgimento dell\'evento' },
            'Organiser la restauration': { title: 'Organizzare catering', description: 'Prevedere cibo e bevande adeguati' },
            'Préparer la décoration': { title: 'Preparare decorazione', description: 'Decorare secondo il tema dell\'evento' }
          }
        };
        
        return translations[userLanguage]?.[key]?.[type] || translations.fr[key]?.[type] || key;
      };
      
      return {
        title: getTranslation(template.title, 'title'),
        description: getTranslation(template.title, 'description'),
        category: template.category,
        priority: template.priority,
        status: 'todo' as const,
        dueDate: adjustedDueDate,
        assignedTo: []
      };
    });
  }
  
  /**
   * Génère les données de tâches légales à partir des templates
   * @param legalTemplates Templates de tâches légales
   * @param eventDate Date de l'événement
   * @param userLanguage Langue de l'utilisateur
   * @returns Array des données de tâches légales à créer
   */
  private generateLegalTasksFromTemplates(legalTemplates: LegalTaskTemplate[], eventDate: Date, userLanguage: string = 'fr'): any[] {
    const now = new Date();
    
    // Fonction pour traduire les tâches légales
    const getLegalTranslation = (key: string, type: 'title' | 'description' = 'title'): string => {
      const legalTranslations: Record<string, Record<string, Record<string, string>>> = {
        fr: {
          'Publier les bans de mariage': {
            title: 'Publier les bans de mariage',
            description: 'Affichage obligatoire des bans à la mairie 10 jours avant le mariage'
          },
          'Constituer le dossier de mariage': {
            title: 'Constituer le dossier de mariage',
            description: 'Rassembler tous les documents requis : actes de naissance, justificatifs de domicile, etc.'
          },
          'Choisir le régime matrimonial': {
            title: 'Choisir le régime matrimonial',
            description: 'Décider du régime matrimonial et éventuellement prendre rendez-vous chez le notaire'
          },
          'Obtenir un certificat médical prénuptial': {
            title: 'Obtenir un certificat médical prénuptial',
            description: 'Bien que non obligatoire depuis 2007, certaines mairies le demandent encore'
          },
          'Déclarer l\'intention de mariage': {
            title: 'Déclarer l\'intention de mariage',
            description: 'Déposer la déclaration d\'intention de mariage au moins 14 jours avant la cérémonie'
          },
          'Fournir les documents d\'état civil': {
            title: 'Fournir les documents d\'état civil',
            description: 'Actes de naissance, certificats de célibat, etc. selon la commune'
          },
          'Choisir le régime matrimonial belge': {
            title: 'Choisir le régime matrimonial belge',
            description: 'Opter pour la communauté légale ou un autre régime devant notaire'
          },
          'Effectuer la préparation au mariage': {
            title: 'Effectuer la préparation au mariage',
            description: 'Procédure préparatoire obligatoire auprès de l\'officier d\'état civil'
          },
          'Publier le mariage': {
            title: 'Publier le mariage',
            description: 'Publication officielle du mariage pendant 10 jours'
          },
          'Présenter les documents d\'identité': {
            title: 'Présenter les documents d\'identité',
            description: 'Passeports ou cartes d\'identité valides des futurs époux'
          },
          'Obtenir une licence de mariage': {
            title: 'Obtenir une licence de mariage',
            description: 'Demander la licence de mariage auprès du bureau d\'état civil provincial'
          },
          'Vérifier les exigences de résidence': {
            title: 'Vérifier les exigences de résidence',
            description: 'S\'assurer de respecter les exigences de résidence de la province'
          },
          'Prévoir les témoins légaux': {
            title: 'Prévoir les témoins légaux',
            description: 'Avoir au moins 2 témoins majeurs présents lors de la cérémonie'
          },
          'Obtenir une marriage license': {
            title: 'Obtenir une licence de mariage',
            description: 'Demander la licence de mariage auprès du bureau du comté'
          },
          'Respecter la période d\'attente': {
            title: 'Respecter la période d\'attente',
            description: 'Certains États imposent une période d\'attente entre la demande et la cérémonie'
          },
          'Vérifier les exigences de l\'État': {
            title: 'Vérifier les exigences de l\'État',
            description: 'Chaque État a ses propres exigences (âge, tests sanguins, etc.)'
          },
          'Vérifier les exigences légales locales': {
            title: 'Vérifier les exigences légales locales',
            description: 'Se renseigner auprès des autorités locales sur les démarches requises'
          },
          'Rassembler les documents d\'identité': {
            title: 'Rassembler les documents d\'identité',
            description: 'Préparer tous les documents d\'identité et d\'état civil nécessaires'
          },
          'Prévoir les témoins requis': {
            title: 'Prévoir les témoins requis',
            description: 'S\'assurer d\'avoir le nombre de témoins requis par la législation locale'
          },
          'Solicitar expediente matrimonial': {
            title: 'Demander le dossier matrimonial',
            description: 'Présenter une demande de dossier matrimonial au Registre Civil'
          },
          'Publicar las amonestaciones': {
            title: 'Publier les bans',
            description: 'Publication des bans pendant 15 jours ouvrés'
          },
          'Presentar documentos de estado civil': {
            title: 'Présenter les documents d\'état civil',
            description: 'Certificats de naissance, célibat et domiciliation'
          },
          'Pubblicare i bandi matrimoniali': {
            title: 'Publier les bans matrimoniaux',
            description: 'Publication des bans pendant 8 jours consécutifs'
          },
          'Presentare documenti di stato civile': {
            title: 'Présenter les documents d\'état civil',
            description: 'Certificats de naissance, état libre et résidence'
          },
          'Scegliere il regime patrimoniale': {
            title: 'Choisir le régime patrimonial',
            description: 'Décider entre communauté ou séparation des biens'
          },
          'Ehefähigkeitszeugnis beantragen': {
            title: 'Demander le certificat de capacité matrimoniale',
            description: 'Demande de certificat de capacité matrimoniale à l\'office d\'état civil'
          },
          'Eheaufgebot veröffentlichen': {
            title: 'Publier les bans de mariage',
            description: 'Publication des bans de mariage pendant 10 jours'
          },
          'Güterstand wählen': {
            title: 'Choisir le régime matrimonial',
            description: 'Décision sur la communauté d\'acquêts ou la séparation de biens'
          },
          'Give notice of marriage': {
            title: 'Déclarer l\'intention de mariage',
            description: 'Déclarer l\'intention au moins 28 jours avant la cérémonie'
          },
          'Obtain marriage license': {
            title: 'Obtenir la licence de mariage',
            description: 'Demander la licence de mariage au bureau d\'enregistrement local'
          },
          'Arrange civil ceremony': {
            title: 'Organiser la cérémonie civile',
            description: 'Réserver la cérémonie civile au bureau d\'enregistrement ou lieu approuvé'
          },
          'Submit marriage notice': {
            title: 'Soumettre l\'avis de mariage',
            description: 'Soumettre l\'avis de mariage au registraire (28 jours avant)'
          },
          'Obtain marriage schedule': {
            title: 'Obtenir le calendrier de mariage',
            description: 'Obtenir le calendrier de mariage du registraire (30 jours avant)'
          },
          'Book ceremony venue': {
            title: 'Réserver le lieu de cérémonie',
            description: 'Réserver le lieu de cérémonie et le célébrant (60 jours avant)'
          }
        },
        en: {
          'Publier les bans de mariage': {
            title: 'Publish marriage banns',
            description: 'Mandatory display of banns at city hall 10 days before the wedding'
          },
          'Constituer le dossier de mariage': {
            title: 'Prepare marriage file',
            description: 'Gather all required documents: birth certificates, proof of residence, etc.'
          },
          'Choisir le régime matrimonial': {
            title: 'Choose matrimonial regime',
            description: 'Decide on matrimonial regime and possibly make an appointment with a notary'
          },
          'Obtenir un certificat médical prénuptial': {
            title: 'Obtain prenuptial medical certificate',
            description: 'Although not mandatory since 2007, some city halls still require it'
          },
          'Déclarer l\'intention de mariage': {
            title: 'Declare intention to marry',
            description: 'File declaration of intention to marry at least 14 days before the ceremony'
          },
          'Fournir les documents d\'état civil': {
            title: 'Provide civil status documents',
            description: 'Birth certificates, single status certificates, etc. according to municipality'
          },
          'Choisir le régime matrimonial belge': {
            title: 'Choose Belgian matrimonial regime',
            description: 'Opt for legal community or another regime before a notary'
          },
          'Effectuer la préparation au mariage': {
            title: 'Complete marriage preparation',
            description: 'Mandatory preparatory procedure with the civil status officer'
          },
          'Publier le mariage': {
            title: 'Publish marriage',
            description: 'Official publication of marriage for 10 days'
          },
          'Présenter les documents d\'identité': {
            title: 'Present identity documents',
            description: 'Valid passports or identity cards of future spouses'
          },
          'Obtenir une licence de mariage': {
            title: 'Obtain marriage license',
            description: 'Apply for marriage license at provincial civil status office'
          },
          'Vérifier les exigences de résidence': {
            title: 'Check residence requirements',
            description: 'Ensure compliance with provincial residence requirements'
          },
          'Prévoir les témoins légaux': {
            title: 'Arrange legal witnesses',
            description: 'Have at least 2 adult witnesses present at the ceremony'
          },
          'Obtenir une marriage license': {
            title: 'Obtain marriage license',
            description: 'Apply for marriage license at County Clerk Office'
          },
          'Respecter la période d\'attente': {
            title: 'Respect waiting period',
            description: 'Some states impose a waiting period between application and ceremony'
          },
          'Vérifier les exigences de l\'État': {
            title: 'Check state requirements',
            description: 'Each state has its own requirements (age, blood tests, etc.)'
          },
          'Vérifier les exigences légales locales': {
            title: 'Check local legal requirements',
            description: 'Inquire with local authorities about required procedures'
          },
          'Rassembler les documents d\'identité': {
            title: 'Gather identity documents',
            description: 'Prepare all necessary identity and civil status documents'
          },
          'Prévoir les témoins requis': {
            title: 'Arrange required witnesses',
            description: 'Ensure you have the number of witnesses required by local legislation'
          },
          'Solicitar expediente matrimonial': {
            title: 'Request marriage file',
            description: 'Submit marriage file request at Civil Registry'
          },
          'Publicar las amonestaciones': {
            title: 'Publish banns',
            description: 'Publication of banns for 15 business days'
          },
          'Presentar documentos de estado civil': {
            title: 'Present civil status documents',
            description: 'Birth certificates, single status and residence certificates'
          },
          'Pubblicare i bandi matrimoniali': {
            title: 'Publish marriage banns',
            description: 'Publication of banns for 8 consecutive days'
          },
          'Presentare documenti di stato civile': {
            title: 'Present civil status documents',
            description: 'Birth certificates, single status and residence certificates'
          },
          'Scegliere il regime patrimoniale': {
            title: 'Choose property regime',
            description: 'Decide between community or separation of property'
          },
          'Ehefähigkeitszeugnis beantragen': {
            title: 'Apply for marriage certificate',
            description: 'Apply for marriage certificate at civil status office'
          },
          'Eheaufgebot veröffentlichen': {
            title: 'Publish marriage banns',
            description: 'Publication of marriage banns for 10 days'
          },
          'Güterstand wählen': {
            title: 'Choose matrimonial regime',
            description: 'Decision on community of property or separation of assets'
          },
          'Give notice of marriage': {
            title: 'Give notice of marriage',
            description: 'Give notice at least 28 days before the ceremony'
          },
          'Obtain marriage license': {
            title: 'Obtain marriage license',
            description: 'Apply for marriage license at local registry office'
          },
          'Arrange civil ceremony': {
            title: 'Arrange civil ceremony',
            description: 'Book civil ceremony at registry office or approved venue'
          },
          'Submit marriage notice': {
            title: 'Submit marriage notice',
            description: 'Submit marriage notice to registrar (28 days before)'
          },
          'Obtain marriage schedule': {
            title: 'Obtain marriage schedule',
            description: 'Obtain marriage schedule from registrar (30 days before)'
          },
          'Book ceremony venue': {
            title: 'Book ceremony venue',
            description: 'Book ceremony venue and celebrant (60 days before)'
          }
        },
        es: {
          'Publier les bans de mariage': {
            title: 'Publicar las amonestaciones matrimoniales',
            description: 'Exposición obligatoria de amonestaciones en el ayuntamiento 10 días antes de la boda'
          },
          'Constituer le dossier de mariage': {
            title: 'Preparar expediente matrimonial',
            description: 'Reunir todos los documentos requeridos: certificados de nacimiento, justificantes de domicilio, etc.'
          },
          'Choisir le régime matrimonial': {
            title: 'Elegir régimen matrimonial',
            description: 'Decidir el régimen matrimonial y posiblemente concertar cita con notario'
          },
          'Obtenir un certificat médical prénuptial': {
            title: 'Obtener certificado médico prenupcial',
            description: 'Aunque no es obligatorio desde 2007, algunos ayuntamientos aún lo requieren'
          },
          'Déclarer l\'intention de mariage': {
            title: 'Declarar intención de matrimonio',
            description: 'Presentar declaración de intención de matrimonio al menos 14 días antes de la ceremonia'
          },
          'Fournir les documents d\'état civil': {
            title: 'Proporcionar documentos de estado civil',
            description: 'Certificados de nacimiento, certificados de soltería, etc. según el municipio'
          },
          'Choisir le régime matrimonial belge': {
            title: 'Elegir régimen matrimonial belga',
            description: 'Optar por la comunidad legal u otro régimen ante notario'
          },
          'Effectuer la préparation au mariage': {
            title: 'Completar preparación matrimonial',
            description: 'Procedimiento preparatorio obligatorio con el oficial de estado civil'
          },
          'Publier le mariage': {
            title: 'Publicar matrimonio',
            description: 'Publicación oficial del matrimonio durante 10 días'
          },
          'Présenter les documents d\'identité': {
            title: 'Presentar documentos de identidad',
            description: 'Pasaportes o tarjetas de identidad válidas de los futuros cónyuges'
          },
          'Obtenir une licence de mariage': {
            title: 'Obtener licencia matrimonial',
            description: 'Solicitar licencia matrimonial en la oficina de estado civil provincial'
          },
          'Vérifier les exigences de résidence': {
            title: 'Verificar requisitos de residencia',
            description: 'Asegurar el cumplimiento de los requisitos de residencia provincial'
          },
          'Prévoir les témoins légaux': {
            title: 'Organizar testigos legales',
            description: 'Tener al menos 2 testigos adultos presentes en la ceremonia'
          },
          'Obtenir une marriage license': {
            title: 'Obtener licencia matrimonial',
            description: 'Solicitar licencia matrimonial en la Oficina del Secretario del Condado'
          },
          'Respecter la période d\'attente': {
            title: 'Respetar período de espera',
            description: 'Algunos estados imponen un período de espera entre la solicitud y la ceremonia'
          },
          'Vérifier les exigences de l\'État': {
            title: 'Verificar requisitos estatales',
            description: 'Cada estado tiene sus propios requisitos (edad, análisis de sangre, etc.)'
          },
          'Vérifier les exigences légales locales': {
            title: 'Verificar requisitos legales locales',
            description: 'Consultar con autoridades locales sobre procedimientos requeridos'
          },
          'Rassembler les documents d\'identité': {
            title: 'Reunir documentos de identidad',
            description: 'Preparar todos los documentos de identidad y estado civil necesarios'
          },
          'Prévoir les témoins requis': {
            title: 'Organizar testigos requeridos',
            description: 'Asegurar tener el número de testigos requeridos por la legislación local'
          },
          'Solicitar expediente matrimonial': {
            title: 'Solicitar expediente matrimonial',
            description: 'Presentar solicitud de expediente matrimonial en el Registro Civil'
          },
          'Publicar las amonestaciones': {
            title: 'Publicar las amonestaciones',
            description: 'Publicación de amonestaciones durante 15 días hábiles'
          },
          'Presentar documentos de estado civil': {
            title: 'Presentar documentos de estado civil',
            description: 'Certificados de nacimiento, soltería y empadronamiento'
          },
          'Pubblicare i bandi matrimoniali': {
            title: 'Publicar las amonestaciones matrimoniales',
            description: 'Publicación de amonestaciones durante 8 días consecutivos'
          },
          'Presentare documenti di stato civile': {
            title: 'Presentar documentos de estado civil',
            description: 'Certificados de nacimiento, estado libre y residencia'
          },
          'Scegliere il regime patrimoniale': {
            title: 'Elegir régimen patrimonial',
            description: 'Decidir entre comunidad o separación de bienes'
          },
          'Ehefähigkeitszeugnis beantragen': {
            title: 'Solicitar certificado de capacidad matrimonial',
            description: 'Solicitar certificado de capacidad matrimonial en la oficina de estado civil'
          },
          'Eheaufgebot veröffentlichen': {
            title: 'Publicar amonestaciones matrimoniales',
            description: 'Publicación de amonestaciones matrimoniales durante 10 días'
          },
          'Güterstand wählen': {
            title: 'Elegir régimen matrimonial',
            description: 'Decisión sobre comunidad de bienes o separación de activos'
          },
          'Give notice of marriage': {
            title: 'Declarar intención de matrimonio',
            description: 'Declarar intención al menos 28 días antes de la ceremonia'
          },
          'Obtain marriage license': {
            title: 'Obtener licencia matrimonial',
            description: 'Solicitar licencia matrimonial en la oficina de registro local'
          },
          'Arrange civil ceremony': {
            title: 'Organizar ceremonia civil',
            description: 'Reservar ceremonia civil en oficina de registro o lugar aprobado'
          },
          'Submit marriage notice': {
            title: 'Presentar aviso de matrimonio',
            description: 'Presentar aviso de matrimonio al registrador (28 días antes)'
          },
          'Obtain marriage schedule': {
            title: 'Obtener calendario matrimonial',
            description: 'Obtener calendario matrimonial del registrador (30 días antes)'
          },
          'Book ceremony venue': {
            title: 'Reservar lugar de ceremonia',
            description: 'Reservar lugar de ceremonia y celebrante (60 días antes)'
          }
        },
        de: {
          'Publier les bans de mariage': {
            title: 'Eheaufgebote veröffentlichen',
            description: 'Verpflichtende Anzeige der Eheaufgebote im Rathaus 10 Tage vor der Hochzeit'
          },
          'Constituer le dossier de mariage': {
            title: 'Heiratsakte vorbereiten',
            description: 'Alle erforderlichen Dokumente sammeln: Geburtsurkunden, Wohnsitznachweise, etc.'
          },
          'Choisir le régime matrimonial': {
            title: 'Ehegüterstand wählen',
            description: 'Ehegüterstand entscheiden und eventuell Termin beim Notar vereinbaren'
          },
          'Obtenir un certificat médical prénuptial': {
            title: 'Voreheliches Gesundheitszeugnis erhalten',
            description: 'Obwohl seit 2007 nicht mehr verpflichtend, verlangen einige Rathäuser es noch'
          },
          'Déclarer l\'intention de mariage': {
            title: 'Heiratsabsicht erklären',
            description: 'Heiratsabsichtserklärung mindestens 14 Tage vor der Zeremonie einreichen'
          },
          'Fournir les documents d\'état civil': {
            title: 'Personenstandsdokumente vorlegen',
            description: 'Geburtsurkunden, Ledigkeitszeugnisse, etc. je nach Gemeinde'
          },
          'Choisir le régime matrimonial belge': {
            title: 'Belgischen Ehegüterstand wählen',
            description: 'Für gesetzliche Gütergemeinschaft oder anderen Güterstand vor Notar entscheiden'
          },
          'Effectuer la préparation au mariage': {
            title: 'Ehevorbereitung durchführen',
            description: 'Verpflichtendes Vorbereitungsverfahren beim Standesbeamten'
          },
          'Publier le mariage': {
            title: 'Ehe veröffentlichen',
            description: 'Offizielle Veröffentlichung der Ehe für 10 Tage'
          },
          'Présenter les documents d\'identité': {
            title: 'Identitätsdokumente vorlegen',
            description: 'Gültige Pässe oder Personalausweise der zukünftigen Ehepartner'
          },
          'Obtenir une licence de mariage': {
            title: 'Heiratslizenz erhalten',
            description: 'Heiratslizenz bei der Provinzstandesamt beantragen'
          },
          'Vérifier les exigences de résidence': {
            title: 'Wohnsitzanforderungen prüfen',
            description: 'Einhaltung der Provinzwohnsitzanforderungen sicherstellen'
          },
          'Prévoir les témoins légaux': {
            title: 'Gesetzliche Zeugen organisieren',
            description: 'Mindestens 2 erwachsene Zeugen bei der Zeremonie haben'
          },
          'Obtenir une marriage license': {
            title: 'Heiratslizenz erhalten',
            description: 'Heiratslizenz beim County Clerk Office beantragen'
          },
          'Respecter la période d\'attente': {
            title: 'Wartezeit respektieren',
            description: 'Einige Bundesstaaten verhängen eine Wartezeit zwischen Antrag und Zeremonie'
          },
          'Vérifier les exigences de l\'État': {
            title: 'Bundesstaatliche Anforderungen prüfen',
            description: 'Jeder Bundesstaat hat seine eigenen Anforderungen (Alter, Bluttests, etc.)'
          },
          'Vérifier les exigences légales locales': {
            title: 'Lokale rechtliche Anforderungen prüfen',
            description: 'Bei lokalen Behörden nach erforderlichen Verfahren erkundigen'
          },
          'Rassembler les documents d\'identité': {
            title: 'Identitätsdokumente sammeln',
            description: 'Alle notwendigen Identitäts- und Personenstandsdokumente vorbereiten'
          },
          'Prévoir les témoins requis': {
            title: 'Erforderliche Zeugen organisieren',
            description: 'Sicherstellen, dass die Anzahl der von der lokalen Gesetzgebung geforderten Zeugen vorhanden ist'
          },
          'Solicitar expediente matrimonial': {
            title: 'Heiratsakte beantragen',
            description: 'Antrag auf Heiratsakte beim Standesamt einreichen'
          },
          'Publicar las amonestaciones': {
            title: 'Eheaufgebote veröffentlichen',
            description: 'Veröffentlichung der Eheaufgebote für 15 Werktage'
          },
          'Presentar documentos de estado civil': {
            title: 'Personenstandsdokumente vorlegen',
            description: 'Geburtsurkunden, Ledigkeitszeugnisse und Meldebescheinigungen'
          },
          'Pubblicare i bandi matrimoniali': {
            title: 'Eheaufgebote veröffentlichen',
            description: 'Veröffentlichung der Eheaufgebote für 8 aufeinanderfolgende Tage'
          },
          'Presentare documenti di stato civile': {
            title: 'Personenstandsdokumente vorlegen',
            description: 'Geburtsurkunden, Ledigkeitszeugnisse und Meldebescheinigungen'
          },
          'Scegliere il regime patrimoniale': {
            title: 'Güterstand wählen',
            description: 'Entscheidung zwischen Gütergemeinschaft oder Gütertrennung'
          },
          'Ehefähigkeitszeugnis beantragen': {
            title: 'Ehefähigkeitszeugnis beantragen',
            description: 'Antrag auf Ehefähigkeitszeugnis beim Standesamt'
          },
          'Eheaufgebot veröffentlichen': {
            title: 'Eheaufgebot veröffentlichen',
            description: 'Veröffentlichung des Eheaufgebots für 10 Tage'
          },
          'Güterstand wählen': {
            title: 'Güterstand wählen',
            description: 'Entscheidung über Zugewinngemeinschaft oder Gütertrennung'
          },
          'Give notice of marriage': {
            title: 'Heiratsabsicht erklären',
            description: 'Heiratsabsicht mindestens 28 Tage vor der Zeremonie erklären'
          },
          'Obtain marriage license': {
            title: 'Heiratslizenz erhalten',
            description: 'Heiratslizenz beim örtlichen Standesamt beantragen'
          },
          'Arrange civil ceremony': {
            title: 'Zivilzeremonie arrangieren',
            description: 'Zivilzeremonie beim Standesamt oder genehmigten Ort buchen'
          },
          'Submit marriage notice': {
            title: 'Heiratsanzeige einreichen',
            description: 'Heiratsanzeige beim Standesbeamten einreichen (28 Tage vorher)'
          },
          'Obtain marriage schedule': {
            title: 'Heiratsterminplan erhalten',
            description: 'Heiratsterminplan vom Standesbeamten erhalten (30 Tage vorher)'
          },
          'Book ceremony venue': {
            title: 'Zeremonieort buchen',
            description: 'Zeremonieort und Trauzeugen buchen (60 Tage vorher)'
          }
        },
        it: {
          'Publier les bans de mariage': {
            title: 'Pubblicare i bandi matrimoniali',
            description: 'Esposizione obbligatoria dei bandi in comune 10 giorni prima del matrimonio'
          },
          'Constituer le dossier de mariage': {
            title: 'Preparare pratica matrimoniale',
            description: 'Raccogliere tutti i documenti richiesti: certificati di nascita, giustificativi di domicilio, etc.'
          },
          'Choisir le régime matrimonial': {
            title: 'Scegliere regime matrimoniale',
            description: 'Decidere il regime matrimoniale ed eventualmente prendere appuntamento dal notario'
          },
          'Obtenir un certificat médical prénuptial': {
            title: 'Ottenere certificato medico prematrimoniale',
            description: 'Anche se non obbligatorio dal 2007, alcuni comuni lo richiedono ancora'
          },
          'Déclarer l\'intention de mariage': {
            title: 'Dichiarare intenzione di matrimonio',
            description: 'Depositare dichiarazione di intenzione di matrimonio almeno 14 giorni prima della cerimonia'
          },
          'Fournir les documents d\'état civil': {
            title: 'Fornire documenti di stato civile',
            description: 'Certificati di nascita, certificati di celibato, etc. secondo il comune'
          },
          'Choisir le régime matrimonial belge': {
            title: 'Scegliere regime matrimoniale belga',
            description: 'Optare per la comunione legale o altro regime davanti al notario'
          },
          'Effectuer la préparation au mariage': {
            title: 'Completare preparazione matrimoniale',
            description: 'Procedura preparatoria obbligatoria presso l\'ufficiale di stato civile'
          },
          'Publier le mariage': {
            title: 'Pubblicare matrimonio',
            description: 'Pubblicazione ufficiale del matrimonio per 10 giorni'
          },
          'Présenter les documents d\'identité': {
            title: 'Presentare documenti di identità',
            description: 'Passaporti o carte d\'identità valide dei futuri coniugi'
          },
          'Obtenir une licence de mariage': {
            title: 'Ottenere licenza matrimoniale',
            description: 'Richiedere licenza matrimoniale presso l\'ufficio di stato civile provinciale'
          },
          'Vérifier les exigences de résidence': {
            title: 'Verificare requisiti di residenza',
            description: 'Assicurarsi di rispettare i requisiti di residenza provinciali'
          },
          'Prévoir les témoins légaux': {
            title: 'Organizzare testimoni legali',
            description: 'Avere almeno 2 testimoni adulti presenti alla cerimonia'
          },
          'Obtenir une marriage license': {
            title: 'Ottenere licenza matrimoniale',
            description: 'Richiedere licenza matrimoniale presso l\'Ufficio del Segretario della Contea'
          },
          'Respecter la période d\'attente': {
            title: 'Rispettare periodo di attesa',
            description: 'Alcuni stati impongono un periodo di attesa tra richiesta e cerimonia'
          },
          'Vérifier les exigences de l\'État': {
            title: 'Verificare requisiti statali',
            description: 'Ogni stato ha i suoi requisiti (età, esami del sangue, etc.)'
          },
          'Vérifier les exigences légales locales': {
            title: 'Verificare requisiti legali locali',
            description: 'Informarsi presso le autorità locali sui procedimenti richiesti'
          },
          'Rassembler les documents d\'identité': {
            title: 'Raccogliere documenti di identità',
            description: 'Preparare tutti i documenti di identità e stato civile necessari'
          },
          'Prévoir les témoins requis': {
            title: 'Organizzare testimoni richiesti',
            description: 'Assicurarsi di avere il numero di testimoni richiesto dalla legislazione locale'
          },
          'Solicitar expediente matrimonial': {
            title: 'Richiedere pratica matrimoniale',
            description: 'Presentare richiesta di pratica matrimoniale presso l\'Ufficio di Stato Civile'
          },
          'Publicar las amonestaciones': {
            title: 'Pubblicare i bandi',
            description: 'Pubblicazione dei bandi per 15 giorni lavorativi'
          },
          'Presentar documentos de estado civil': {
            title: 'Presentare documenti di stato civile',
            description: 'Certificati di nascita, stato libero e residenza'
          },
          'Pubblicare i bandi matrimoniali': {
            title: 'Pubblicare i bandi matrimoniali',
            description: 'Pubblicazione dei bandi per 8 giorni consecutivi'
          },
          'Presentare documenti di stato civile': {
            title: 'Presentare documenti di stato civile',
            description: 'Certificati di nascita, stato libero e residenza'
          },
          'Scegliere il regime patrimoniale': {
            title: 'Scegliere il regime patrimoniale',
            description: 'Decidere tra comunione o separazione dei beni'
          },
          'Ehefähigkeitszeugnis beantragen': {
            title: 'Richiedere certificato di capacità matrimoniale',
            description: 'Richiedere certificato di capacità matrimoniale presso l\'ufficio di stato civile'
          },
          'Eheaufgebot veröffentlichen': {
            title: 'Pubblicare i bandi matrimoniali',
            description: 'Pubblicazione dei bandi matrimoniali per 10 giorni'
          },
          'Güterstand wählen': {
            title: 'Scegliere regime matrimoniale',
            description: 'Decisione sulla comunione dei beni o separazione dei patrimoni'
          },
          'Give notice of marriage': {
            title: 'Dichiarare intenzione di matrimonio',
            description: 'Dichiarare intenzione almeno 28 giorni prima della cerimonia'
          },
          'Obtain marriage license': {
            title: 'Ottenere licenza matrimoniale',
            description: 'Richiedere licenza matrimoniale presso l\'ufficio di registro locale'
          },
          'Arrange civil ceremony': {
            title: 'Organizzare cerimonia civile',
            description: 'Prenotare cerimonia civile presso ufficio di registro o luogo approvato'
          },
          'Submit marriage notice': {
            title: 'Presentare avviso di matrimonio',
            description: 'Presentare avviso di matrimonio al registratore (28 giorni prima)'
          },
          'Obtain marriage schedule': {
            title: 'Ottenere calendario matrimoniale',
            description: 'Ottenere calendario matrimoniale dal registratore (30 giorni prima)'
          },
          'Book ceremony venue': {
            title: 'Prenotare luogo di cerimonia',
            description: 'Prenotare luogo di cerimonia e celebrante (60 giorni prima)'
          }
        }
      };
      
      return legalTranslations[userLanguage]?.[key]?.[type] || legalTranslations.fr[key][type] || key;
    };
    
    return legalTemplates.map(template => {
      // Calculer la date d'échéance
      const dueDate = new Date(eventDate);
      dueDate.setDate(dueDate.getDate() - template.daysBeforeEvent);
      
      // Si la date d'échéance est dans le passé, l'ajuster à aujourd'hui + quelques jours
      const adjustedDueDate = dueDate < now 
        ? new Date(now.getTime() + (3 * 24 * 60 * 60 * 1000)) // 3 jours à partir d'aujourd'hui
        : dueDate;
      
      const translatedTitle = getLegalTranslation(template.title, 'title');
      const translatedDescription = getLegalTranslation(template.title, 'description');
      
      return {
        title: `⚖️ ${translatedTitle}`,
        description: `${translatedDescription}${template.isRequired ? ' (OBLIGATOIRE)' : ' (Recommandé)'}`,
        category: template.category,
        priority: template.isRequired ? 'high' : template.priority,
        status: 'todo' as const,
        dueDate: adjustedDueDate,
        assignedTo: []
      };
    });
  }
  
  /**
   * Vérifie si un événement a déjà des tâches générées
   * @param eventId ID de l'événement
   * @param eventType Type d'événement
   * @returns true si des tâches existent déjà
   */
  async hasExistingTasks(eventId: string, eventType: string): Promise<boolean> {
    try {
      const existingTasks = await genericTasksService.getEventTasks(eventId, eventType);
      return existingTasks.length > 0;
    } catch (error) {
      console.error(`❌ [TaskGenerator] Error checking existing tasks:`, error);
      return false;
    }
  }
  
  /**
   * Génère les tâches seulement si aucune tâche n'existe pour l'événement
   * @param eventId ID de l'événement
   * @param eventType Type d'événement
   * @param eventDate Date de l'événement
   * @param country Code pays pour les tâches légales (optionnel)
   * @param forceRegeneration Forcer la régénération même si des tâches existent (défaut: false)
   */
  async generateTasksIfNeeded(
    eventId: string, 
    eventType: string, 
    eventDate: Date, 
    country?: string,
    userLanguage: string = 'fr',
    forceRegeneration: boolean = false
  ): Promise<void> {
    const cacheKey = `${eventId}_${eventType}`;

    // Si on force la régénération, nettoyer le cache pour cet événement
    if (forceRegeneration) {
      this.checkedEvents.delete(cacheKey);
    }

    // Vérifier le cache pour éviter les vérifications répétées
    if (this.checkedEvents.has(cacheKey)) {
      return;
    }

    const hasExisting = await this.hasExistingTasks(eventId, eventType);

    if (hasExisting && !forceRegeneration) {
      // Marquer comme vérifié pour éviter les futures vérifications
      this.checkedEvents.add(cacheKey);
      return;
    }

    if (forceRegeneration && hasExisting) {}

    await this.generateTasksForEvent(eventId, eventType, eventDate, country, userLanguage);

    // Marquer comme vérifié après génération
    this.checkedEvents.add(cacheKey);
  }

  /**
   * Force la régénération des tâches pour un événement (en évitant les doublons)
   * @param eventId ID de l'événement
   * @param eventType Type d'événement
   * @param eventDate Date de l'événement
   * @param country Code pays pour les tâches légales (optionnel)
   */
  async forceRegenerateTasksIfMissing(
    eventId: string, 
    eventType: string, 
    eventDate: Date, 
    country?: string
  ): Promise<void> {
    await this.generateTasksIfNeeded(eventId, eventType, eventDate, country, true);
  }

  /**
   * Nettoyer le cache (utile pour les tests ou après certaines opérations)
   */
  clearCache(): void {
    this.checkedEvents.clear();
  }
}

// Instance singleton
export const eventTaskGeneratorService = new EventTaskGeneratorService(); 